import { Spinner } from '@heroui/spinner'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { z } from 'zod'

import { WorkflowRoute } from '@/app/routes/_app/workflows/workflows'
import { getUserInfo, verifyEmail } from '@/features/auth'
import { SignInPage } from '@/pages/sign-in'
import { isSafeRedirectPath } from '@/shared/lib/auth'
import { FullPageWrapper } from '@/shared/ui'

import { InvitationSignUpRoute } from './invitation-accept'

const searchSchemaValidation = z.object({
  token: z.string().optional(),
  inviteToken: z.string().optional(),
  redirectTo: z.string().optional(),
})
type SearchParams = z.infer<typeof searchSchemaValidation>
type VerificationResult = { status: 'success' } | { status: 'error' } | { status: 'none' }

export const Route = createFileRoute('/sign-in')({
  component: RouteComponent,
  validateSearch: (search): SearchParams => searchSchemaValidation.parse(search),
  loaderDeps: ({ search: { token, inviteToken, redirectTo } }) => ({ token, inviteToken, redirectTo }),
  loader: async ({ deps }): Promise<VerificationResult> => {
    try {
      const userInfo = await getUserInfo()
      if (userInfo && deps.inviteToken) {
        return redirect({ to: InvitationSignUpRoute.to, search: { token: deps.inviteToken } }) as never
      }
      if (userInfo && isSafeRedirectPath(deps.redirectTo)) {
        return redirect({ href: deps.redirectTo }) as never
      }
      if (userInfo) {
        return redirect({ to: WorkflowRoute.to }) as never
      }
    } catch {
      // 🏡 stay on signup page
    }

    const { token } = deps
    if (!token) {
      return { status: 'none' }
    }

    const emailStatus = await verifyEmail(token)
    return { status: emailStatus }
  },
  pendingComponent: () => (
    <FullPageWrapper>
      <div className="flex w-full flex-col items-center justify-center">
        <Spinner size="lg" />
      </div>
    </FullPageWrapper>
  ),
})

export const SignInRoute = Route

function RouteComponent() {
  const { status } = SignInRoute.useLoaderData()
  return <SignInPage status={status} />
}
